<template>
  <div class="flex flex-col sm:flex-row sm:items-start gap-3 flex-wrap">
    <div class="w-full sm:w-auto min-h-[56px]">
      <DatePicker
        :model-value="startDate"
        @update:model-value="emit('update:startDate', $event)"
        locale="pt-BR"
        :masks="{ input: 'DD/MM/YYYY' }"
        :disabled="indefiniteEndDate"
        :max-date="maxDate"
        class="w-full h-full"
      >
        <template #default="{ inputValue, inputEvents }">
          <v-text-field
            :data-testid="`${testidPrefix}-start-date-input`"
            placeholder="Início da vigência"
            :append-inner-icon="mdiCalendar"
            :value="inputValue"
            v-on="inputEvents"
            density="comfortable"
            variant="outlined"
            color="primary20"
            class="w-full sm:w-[192px] h-full"
            :error="!!errors?.startDate"
            :error-messages="errors?.startDate"
            @update:model-value="emit('clearError', 'startDate')"
          />
        </template>
      </DatePicker>
    </div>

    <div class="w-full sm:w-auto min-h-[56px]">
      <DatePicker
        :model-value="endDate"
        @update:model-value="emit('update:endDate', $event)"
        locale="pt-BR"
        :masks="{ input: 'DD/MM/YYYY' }"
        :disabled="indefiniteEndDate"
        :max-date="maxDate"
        class="w-full h-full"
      >
        <template #default="{ inputValue, inputEvents }">
          <v-text-field
            :data-testid="`${testidPrefix}-end-date-input`"
            placeholder="Fim da vigência"
            :append-inner-icon="mdiCalendar"
            :value="inputValue"
            v-on="inputEvents"
            density="comfortable"
            variant="outlined"
            color="primary20"
            class="w-full sm:w-[192px] h-full"
            :disabled="indefiniteEndDate"
            :error="!!errors?.endDate"
            :error-messages="errors?.endDate"
            @update:model-value="emit('clearError', 'endDate')"
          />
        </template>
      </DatePicker>
    </div>

    <div class="flex items-center gap-2 h-[48px] w-full sm:w-auto">
      <DsSwitch
        :data-testid="`${testidPrefix}-indeterminate-switch`"
        :model-value="indefiniteEndDate"
        label="Fim de vigência indeterminado"
        @update:model-value="onIndefiniteEndDateChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch } from 'vue'
import { DatePicker } from 'v-calendar'
import { mdiCalendar } from '@mdi/js'
import DsSwitch from '@/components/designSystem/Switch/index.vue'

const props = defineProps<{
  startDate: Date | null
  endDate: Date | null
  indefiniteEndDate: boolean
  maxDate?: Date | null
  errors?: Record<string, string>
  testidPrefix: string
}>()

const emit = defineEmits<{
  'update:startDate': [Date | null]
  'update:endDate': [Date | null]
  'update:indefiniteEndDate': [boolean]
  clearError: [field: 'startDate' | 'endDate']
}>()

// Mesma dupla-emissao de clearError('endDate') que existia no componente pai
// (listener explicito no DsSwitch + watch reativo do valor) — quirk preservada.
const onIndefiniteEndDateChange = (value: boolean) => {
  emit('update:indefiniteEndDate', value)
  emit('clearError', 'endDate')
}

watch(
  () => props.indefiniteEndDate,
  (isIndefinite) => {
    if (isIndefinite) {
      emit('update:endDate', null)
      emit('clearError', 'endDate')
    }
  }
)
</script>
